import { useCallback, useState } from 'react';
import { CalendarClock, ChevronDown, ChevronRight } from 'lucide-react';
import { periodReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  fmtDate, fmtAmtZero, EmptyState,
} from '@/components/analytics/AnalyticsKit';
import {
  Pill, Pager, Dash, TabBar, errMessage, DEFAULT_PAGE_SIZE,
} from './ReportCentreKit';
import type { PagedResult } from '@/types/api.types';
import type {
  PeriodStatusReportDto, PeriodStatusRow,
  PostCloseTransactionsFilter, PostCloseTransactionRow,
} from '@/types/reportCentre.types';

const REPORT_KEY = 'PERIOD_STATUS';

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Compact per-module open/closed pill. */
const ModulePill = ({ label, closed }: { label: string; closed: boolean }) => (
  <Pill label={label} tone={closed ? 'red' : 'green'} title={`${label} — ${closed ? 'Closed' : 'Open'}`} />
);

// ── Export ────────────────────────────────────────────────────────────────────

const STATUS_EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Code',      header: 'Period Code',     width: 14 },
  { key: 'Name',      header: 'Period Name',     width: 20 },
  { key: 'Num',       header: 'Period No',       width: 10 },
  { key: 'Start',     header: 'Start',           width: 12 },
  { key: 'End',       header: 'End',             width: 12 },
  { key: 'Posting',   header: 'Posting Allowed', width: 14 },
  { key: 'Gl',        header: 'GL',              width: 8 },
  { key: 'Ar',        header: 'AR',              width: 8 },
  { key: 'Ap',        header: 'AP',              width: 8 },
  { key: 'Bank',      header: 'Banking',         width: 10 },
  { key: 'Sales',     header: 'Sales',           width: 8 },
  { key: 'Inv',       header: 'Inventory',       width: 10 },
  { key: 'ClosedBy',  header: 'Closed By',       width: 18 },
  { key: 'ClosedAt',  header: 'Closed At',       width: 18 },
  { key: 'ReopenBy',  header: 'Reopened By',     width: 18 },
  { key: 'ReopenAt',  header: 'Reopened At',     width: 18 },
  { key: 'Remarks',   header: 'Remarks',         width: 40 },
];

function buildStatusExportRows(rows: PeriodStatusRow[]): Record<string, unknown>[] {
  return rows.map(p => ({
    Code:     p.periodCode,
    Name:     p.periodName,
    Num:      p.periodNumber,
    Start:    p.startDate,
    End:      p.endDate,
    Posting:  p.postingAllowed ? 'Yes' : 'No',
    Gl:       p.isGlClosed ? 'Closed' : 'Open',
    Ar:       p.isArClosed ? 'Closed' : 'Open',
    Ap:       p.isApClosed ? 'Closed' : 'Open',
    Bank:     p.isBankingClosed ? 'Closed' : 'Open',
    Sales:    p.isSalesClosed ? 'Closed' : 'Open',
    Inv:      p.isInventoryClosed ? 'Closed' : 'Open',
    ClosedBy: p.closedByName ?? '',
    ClosedAt: p.closedDate ?? '',
    ReopenBy: p.reopenedByName ?? '',
    ReopenAt: p.reopenedDate ?? '',
    Remarks:  p.remarks ?? '',
  }));
}

const POSTCLOSE_EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Journal',    header: 'Journal',          width: 18 },
  { key: 'PostDate',   header: 'Posting Date',     width: 12 },
  { key: 'PostedAt',   header: 'Posted At',        width: 18 },
  { key: 'Period',     header: 'Period',           width: 12 },
  { key: 'Closed',     header: 'Period Closed',    width: 18 },
  { key: 'Days',       header: 'Days After Close', width: 14 },
  { key: 'ReopenAt',   header: 'Reopened At',      width: 18 },
  { key: 'ReopenBy',   header: 'Reopened By',      width: 18 },
  { key: 'CreatedBy',  header: 'Created By',       width: 18 },
  { key: 'PostedBy',   header: 'Posted By',        width: 18 },
  { key: 'Total',      header: 'Total Debit',      width: 16, numFmt: '#,##0.00' },
  { key: 'Source',     header: 'Source Module',    width: 16 },
];

function buildPostCloseExportRows(rows: PostCloseTransactionRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Journal:   r.journalNumber,
    PostDate:  r.postingDate,
    PostedAt:  r.postedDate ?? '',
    Period:    r.periodCode,
    Closed:    r.periodClosedDate ?? '',
    Days:      r.daysAfterClose,
    ReopenAt:  r.reopenedDate ?? '',
    ReopenBy:  r.reopenedByName ?? '',
    CreatedBy: r.createdByName ?? '',
    PostedBy:  r.postedByName ?? '',
    Total:     r.totalDebit,
    Source:    r.sourceModule ?? '',
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const PeriodStatusPage = () => {
  const { hasPermission } = usePermission();

  const [tab, setTab] = useState('status');

  // Tab 1 — period status
  const [financialYear, setFinancialYear] = useState('');
  const [data, setData] = useState<PeriodStatusReportDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Tab 2 — post-close transactions
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [pcData, setPcData] = useState<PagedResult<PostCloseTransactionRow> | null>(null);
  const [pcApplied, setPcApplied] = useState<PostCloseTransactionsFilter | null>(null);
  const [pcLoading, setPcLoading] = useState(false);
  const [pcError, setPcError] = useState<string | null>(null);

  const runStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await periodReportApi.getPeriodStatus({
        financialYear: financialYear.trim() || undefined,
      });
      setData(res.data?.data ?? null);
      setExpanded(new Set());
    } catch (e: unknown) {
      setError(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [financialYear]);

  const fetchPostClose = useCallback(async (filter: PostCloseTransactionsFilter) => {
    setPcLoading(true);
    setPcError(null);
    try {
      const res = await periodReportApi.getPostCloseTransactions(filter);
      setPcData(res.data?.data ?? null);
      setPcApplied(filter);
    } catch (e: unknown) {
      setPcError(errMessage(e));
    } finally {
      setPcLoading(false);
    }
  }, []);

  const runPostClose = useCallback(() => {
    void fetchPostClose({
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    });
  }, [fromDate, toDate, fetchPostClose]);

  const toggleExpand = (periodId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(periodId)) next.delete(periodId);
      else next.add(periodId);
      return next;
    });
  };

  if (!hasPermission(Permissions.RPT_PERIOD_STATUS)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const periods = data?.periods ?? [];
  const pcRows = pcData?.items ?? [];
  const isStatusTab = tab === 'status';

  const handleExcel = () => {
    if (isStatusTab) {
      if (!data) return;
      exportToExcel({
        sheetName: 'Period Status',
        columns: STATUS_EXCEL_COLUMNS,
        rows: buildStatusExportRows(periods),
        title: 'Financial Period Status',
        subtitle: `${data.companyName}${data.financialYearFilter ? ` · FY ${data.financialYearFilter}` : ''}`,
      }, 'PeriodStatus');
    } else {
      if (!pcData) return;
      exportToExcel({
        sheetName: 'Posted After Close',
        columns: POSTCLOSE_EXCEL_COLUMNS,
        rows: buildPostCloseExportRows(pcRows),
        title: 'Transactions Posted After Period Close',
      }, 'PostCloseTransactions');
    }
  };

  const handleCsv = () => {
    if (isStatusTab) {
      if (!data) return;
      exportToCsvRows(STATUS_EXCEL_COLUMNS, buildStatusExportRows(periods), 'PeriodStatus');
    } else {
      if (!pcData) return;
      exportToCsvRows(POSTCLOSE_EXCEL_COLUMNS, buildPostCloseExportRows(pcRows), 'PostCloseTransactions');
    }
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarClock size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Period Status &amp; Post-Close Activity</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={isStatusTab ? !!data : !!pcData}
          reportKey={REPORT_KEY}
          rowCount={isStatusTab ? periods.length : pcRows.length}
          filterSummary={isStatusTab
            ? `financialYear=${financialYear || 'all'}`
            : `from=${fromDate || 'open'} to=${toDate || 'open'}`}
        />
      </div>

      <TabBar
        tabs={[
          { key: 'status', label: 'Period Status' },
          { key: 'postclose', label: 'Posted After Close' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {isStatusTab ? (
        <>
          {/* Filter panel */}
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="w-56">
                <label className={LABEL_CLS}>Financial Year</label>
                <input
                  type="text"
                  value={financialYear}
                  onChange={e => setFinancialYear(e.target.value)}
                  placeholder="e.g. 2026 (blank = all)"
                  className={INPUT_CLS}
                />
              </div>
              <RunReportButton onClick={() => { void runStatus(); }} isLoading={loading} />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {loading && !data && (
            <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
          )}

          {!data && !loading && (
            <EmptyState message='Click "Run Report" to list financial periods with their per-module closing status.' />
          )}

          {data && (
            <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
              <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Period status{data.financialYearFilter ? ` · FY ${data.financialYearFilter}` : ' · all years'}
                  {' · '}Generated {fmtDate(data.generatedAt)} · Expand a row to see module closing entries
                </div>
              </div>
              <div className="overflow-auto">
                <table className="w-full text-sm border-collapse min-w-[1300px]">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                    <tr>
                      <th className={cn(TH_CLS, 'w-8')} />
                      <th className={TH_CLS}>Period</th>
                      <th className={TH_CLS}>Name</th>
                      <th className={cn(TH_R_CLS, 'w-14')}>No</th>
                      <th className={TH_CLS}>Range</th>
                      <th className={TH_CLS}>Posting</th>
                      <th className={TH_CLS}>Modules</th>
                      <th className={TH_CLS}>Closed</th>
                      <th className={TH_CLS}>Reopened</th>
                      <th className={TH_CLS}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periods.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                          No financial periods found for the selected filter.
                        </td>
                      </tr>
                    ) : periods.map(p => {
                      const hasEntries = p.closingEntries.length > 0;
                      const isOpen = expanded.has(p.periodId);
                      return (
                        <PeriodRowGroup
                          key={p.periodId}
                          period={p}
                          hasEntries={hasEntries}
                          isOpen={isOpen}
                          onToggle={() => toggleExpand(p.periodId)}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Filter panel */}
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="w-44">
                  <label className={LABEL_CLS}>Posted From</label>
                  <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className={INPUT_CLS} />
                </div>
                <div className="w-44">
                  <label className={LABEL_CLS}>Posted To</label>
                  <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className={INPUT_CLS} />
                </div>
              </div>
              <RunReportButton onClick={runPostClose} isLoading={pcLoading} />
            </div>
          </div>

          {pcError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {pcError}
            </div>
          )}

          {pcLoading && !pcData && (
            <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
          )}

          {!pcData && !pcLoading && (
            <EmptyState message='Click "Run Report" to list journals that were posted after their period was closed. Dates are optional.' />
          )}

          {pcData && (
            <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
              <div className="overflow-auto">
                <table className="w-full text-sm border-collapse min-w-[1400px]">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                    <tr>
                      <th className={TH_CLS}>Journal</th>
                      <th className={cn(TH_CLS, 'w-28')}>Posting Date</th>
                      <th className={TH_CLS}>Posted At</th>
                      <th className={TH_CLS}>Period</th>
                      <th className={TH_CLS}>Period Closed</th>
                      <th className={cn(TH_R_CLS, 'w-24')}>Days After</th>
                      <th className={TH_CLS}>Reopened</th>
                      <th className={TH_CLS}>Created By</th>
                      <th className={TH_CLS}>Posted By</th>
                      <th className={TH_R_CLS}>Total Debit</th>
                      <th className={TH_CLS}>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pcRows.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                          No journals were posted after their period was closed in the selected window.
                        </td>
                      </tr>
                    ) : pcRows.map(r => (
                      <tr key={r.journalId} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{r.journalNumber}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(r.postingDate)}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {r.postedDate ? fmtDateTime(r.postedDate) : <Dash />}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.periodCode}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {r.periodClosedDate ? fmtDateTime(r.periodClosedDate) : <Dash />}
                        </td>
                        <td className={cn(AMT_CLS, 'font-bold text-red-600 dark:text-red-400')}>{r.daysAfterClose}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          {r.reopenedDate ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Pill label="Reopened" tone="amber" />
                              {fmtDateTime(r.reopenedDate)}{r.reopenedByName ? ` · ${r.reopenedByName}` : ''}
                            </span>
                          ) : <Dash />}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.createdByName ?? <Dash />}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.postedByName ?? <Dash />}</td>
                        <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmtZero(r.totalDebit)}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{r.sourceModule ?? <Dash />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager
                pageNumber={pcData.pageNumber}
                totalPages={pcData.totalPages}
                totalCount={pcData.totalCount}
                shownCount={pcRows.length}
                loading={pcLoading}
                onPage={p => {
                  if (pcApplied) void fetchPostClose({ ...pcApplied, pageNumber: p });
                }}
                noun="journals"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Expandable period row ─────────────────────────────────────────────────────

const PeriodRowGroup = ({ period: p, hasEntries, isOpen, onToggle }: {
  period: PeriodStatusRow;
  hasEntries: boolean;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <>
    <tr
      onClick={hasEntries ? onToggle : undefined}
      className={cn(
        'border-t border-gray-100 dark:border-gray-800',
        hasEntries && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50',
      )}
      title={hasEntries ? 'Show module closing entries' : undefined}
    >
      <td className="px-3 py-2 text-gray-400">
        {hasEntries ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : null}
      </td>
      <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{p.periodCode}</td>
      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">{p.periodName}</td>
      <td className={cn(AMT_CLS, 'text-gray-600 dark:text-gray-400')}>{p.periodNumber}</td>
      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {fmtDate(p.startDate)} – {fmtDate(p.endDate)}
      </td>
      <td className="px-3 py-2">
        <Pill label={p.postingAllowed ? 'Allowed' : 'Blocked'} tone={p.postingAllowed ? 'green' : 'red'} />
      </td>
      <td className="px-3 py-2">
        <span className="inline-flex flex-wrap items-center gap-1">
          <ModulePill label="GL" closed={p.isGlClosed} />
          <ModulePill label="AR" closed={p.isArClosed} />
          <ModulePill label="AP" closed={p.isApClosed} />
          <ModulePill label="Bank" closed={p.isBankingClosed} />
          <ModulePill label="Sales" closed={p.isSalesClosed} />
          <ModulePill label="Inv" closed={p.isInventoryClosed} />
        </span>
      </td>
      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {p.isClosed
          ? <>{p.closedByName ?? 'Unknown'}{p.closedDate ? ` · ${fmtDate(p.closedDate)}` : ''}</>
          : <Dash />}
      </td>
      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {p.reopenedDate ? (
          <span className="inline-flex items-center gap-1.5">
            <Pill label="Reopened" tone="amber" />
            {p.reopenedByName ?? 'Unknown'} · {fmtDate(p.reopenedDate)}
          </span>
        ) : <Dash />}
      </td>
      <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{p.remarks ?? <Dash />}</td>
    </tr>
    {isOpen && hasEntries && (
      <tr className="border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30">
        <td colSpan={10} className="px-6 py-3">
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Module Closing Entries
          </div>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-4 font-medium">Module</th>
                <th className="py-1 pr-4 font-medium">Status</th>
                <th className="py-1 pr-4 font-medium">Close Type</th>
                <th className="py-1 pr-4 font-medium">Workflow</th>
                <th className="py-1 pr-4 font-medium">Closed</th>
                <th className="py-1 pr-4 font-medium">Remarks</th>
                <th className="py-1 pr-4 font-medium">Reopened</th>
                <th className="py-1 font-medium">Reopen Reason</th>
              </tr>
            </thead>
            <tbody>
              {p.closingEntries.map((e, i) => (
                <tr key={`${e.module}-${i}`} className="border-t border-gray-200/60 dark:border-gray-700/60 text-gray-700 dark:text-gray-300">
                  <td className="py-1.5 pr-4 font-medium">{e.module}</td>
                  <td className="py-1.5 pr-4">
                    <Pill label={e.status} tone={e.status === 'Open' ? 'green' : e.status === 'Locked' ? 'purple' : 'red'} />
                  </td>
                  <td className="py-1.5 pr-4">{e.closeType ?? <Dash />}</td>
                  <td className="py-1.5 pr-4">{e.workflowStatus}</td>
                  <td className="py-1.5 pr-4 whitespace-nowrap">
                    {e.closingDate ? <>{fmtDate(e.closingDate)}{e.closedByName ? ` · ${e.closedByName}` : ''}</> : <Dash />}
                  </td>
                  <td className="py-1.5 pr-4">{e.closeRemarks ?? <Dash />}</td>
                  <td className="py-1.5 pr-4 whitespace-nowrap">
                    {e.reopenedDate ? <>{fmtDate(e.reopenedDate)}{e.reopenedByName ? ` · ${e.reopenedByName}` : ''}</> : <Dash />}
                  </td>
                  <td className="py-1.5">{e.reopenReason ?? <Dash />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </td>
      </tr>
    )}
  </>
);
